import { axiosWithRetry } from 'utils/axios';
import { updateLocalStorage } from 'utils/browserCache';
import { getUserIdentifier } from 'utils/id';

import { LOCAL_STORAGE_KEY } from 'constants/browserCache';
import { HTTP_REQUEST_METHODS } from 'constants/http';
import { IDENTIFY_TYPES } from 'constants/id';
import CHAT_BACKEND from 'utils/chatBackend';
import { MojoContextType, NativeAppPutContextType } from 'utils/chatContext/types';
import {
  PropelAuthType,
  PropelInfoType,
  VendorCommandDataType,
  VendorCommandValues,
} from './types';
interface PutContextApiReturn extends MojoContextType {
  retryCount: number;
}

import { VENDOR_COMMANDS } from 'constants/vendor-commands';
import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';

/**
 * @description - put/set context via Propel Mojo gateway for Sebastian AI engine to provide relevant responses.
 *                Can be set any time, on demand. Subsequent engine responses should reflect latest context.
 * {@linkplain https://carvanaassetsdev.blob.core.windows.net/chatai-demo/index.html?advanced=1#/ Context setter (visualizer tool)}
 * @param {object} mojoContext - formatted data to set context for AI Engine responses.
 * @param {object} propelMojoAuth - metadata to initialize conversation. Notably access_token & conversation_id
 * @return {Promise<PutContextApiReturn>} - echo/relay of context for AI conversation with performance metrics.
 */
export const putPropelMojoContext = async (
  mojoContext: MojoContextType | NativeAppPutContextType,
  propelMojoAuth: PropelAuthType,
): Promise<PutContextApiReturn> => {
  const res = await axiosWithRetry({
    body: { context: mojoContext },
    headers: { Authorization: `Bearer ${propelMojoAuth.mojoAccessToken}` },
    method: HTTP_REQUEST_METHODS.PUT,
    url: `${process.env.REACT_APP_PROPEL_CONVERSATIONS_BASE_URL}/${propelMojoAuth.conversationId}/context`,
  });
  const retryCount = res?.config?.['axios-retry']?.retryCount ?? null;
  const { data } = res;
  delete data?.id; // VERY IMPORTANT. This is an operation id that comes from API's echo. Do not parse/persist. Will very likely mess up chatContext on backend.
  updateLocalStorage(LOCAL_STORAGE_KEY.CVNA_PROPEL_CHAT_CONTEXT, data);
  return { ...data, retryCount };
};

/**
 * Executes a Vendor Command
 * @param {string} conversationId Twilio Conversation ID (ex cdc22d01-a1b8-41a8-a73e-7daebd28a4f1)
 * @param {string} twilioConversationSid Twilio Channel ID (ex CHfa1d5d41094c48388028125257e396e3)
 * @param {VendorCommandValues} command Which vendor command to execute
 * @param {string} propelAccessToken Propel Access Token used to execute the vendor command
 * @param data Custom data object passed to the vendor command
 * @returns
 */
export const vendorCommandAsync = async (
  conversationId: string,
  twilioConversationSid: string,
  command: VendorCommandValues,
  propelAccessToken: string,
  data?: VendorCommandDataType,
) => {
  const { browserCookieId, carvanaDeviceId, userIdentifierType } = getUserIdentifier();

  if (userIdentifierType) {
    const body = {
      [IDENTIFY_TYPES.CARVANA_DEVICE_ID]: {
        ...data,
        twilio_channel_id: twilioConversationSid,
        carvana_device_id: carvanaDeviceId,
      } as PropelInfoType,
      [IDENTIFY_TYPES.BROWSER_COOKIE_ID]: {
        ...data,
        twilio_channel_id: twilioConversationSid,
        browser_cookie_id: browserCookieId,
        carvana_browser_cookie_id: browserCookieId,
      } as PropelInfoType,
    };

    const payload = {
      ...body[userIdentifierType],
      conversationId,
    };

    try {
      const { data: result } = await CHAT_BACKEND.executeCommand(
        command,
        payload,
        propelAccessToken,
      );
      DataDogLogger.action(CUSTOM_ACTION_NAMES.CHAT_BE_COMMAND_COMPLETED, { command });
      return result;
    } catch (error) {
      DataDogLogger.error(CUSTOM_ACTION_NAMES.CHAT_BE_COMMAND_FAILED, { command, error });
    }

    // If above fails, fallback to using propel vendor commands to retry  the command
    // Special case since customer journey is a vendor-infos command, not a vendor command
    try {
      if (command === VENDOR_COMMANDS.CARVANA_CUSTOMER_JOURNEY) {
        const url = `${process.env.REACT_APP_BASE_PROPEL_URL}/v1/conversations/${conversationId}/vendor-infos/${VENDOR_COMMANDS.CARVANA_CUSTOMER_JOURNEY}`;

        const result = await axiosWithRetry({
          url,
          method: HTTP_REQUEST_METHODS.GET,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${propelAccessToken}`,
          },
        });
        DataDogLogger.action(CUSTOM_ACTION_NAMES.VENDOR_COMMAND_FALLBACK_COMPLETED, {
          command,
          result,
        });

        return result;
      }

      const result = await axiosWithRetry({
        body: body[userIdentifierType],
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${propelAccessToken}`,
        },
        method: HTTP_REQUEST_METHODS.POST,
        url: `${process.env.REACT_APP_BASE_PROPEL_URL}/v1/conversations/${conversationId}/vendor-commands/${command}`,
      });

      DataDogLogger.action(CUSTOM_ACTION_NAMES.VENDOR_COMMAND_FALLBACK_COMPLETED, {
        command,
        result,
      });

      return result;
    } catch (error) {
      DataDogLogger.action(CUSTOM_ACTION_NAMES.VENDOR_COMMAND_FALLBACK_FAILED, {
        command,
        error,
      });
    }
  }
};
